/* eslint-disable no-console */
import {
    CompressedStorageClient,
    getExternalAssessmentIndexedDBClient,
    StorageClient,
    decompress,
} from '@amplify/web-assessment-common/dist/storage';
import pickBy from 'lodash.pickby';
import flow from 'lodash.flow';
import mapValues from 'lodash.mapvalues';
import values from 'lodash.values';

const filterKeys = (dataRaw, blackListKeys, outputKeysMap) => {
    const data = outputKeysMap ? {} : dataRaw;

    if (outputKeysMap) {
        Object.keys(dataRaw).forEach(key => {
            data[outputKeysMap[key] || key] = dataRaw[key];
        });
    }

    return pickBy(data, (value, key) => !blackListKeys.includes(key));
};

class AssessmentStorageClient {
    RESULTS_BLACKLIST_KEYS = ['_persist'];

    RESULTS_BLACKLIST_DETAILS_KEYS = ['itemLevelDetails'];

    FORMS_BLACKLIST_DETAILS_KEYS = [];

    CUTSCORES_BLACKLIST_DETAILS_KEYS = [];

    CUTSCORES_OUTPUT_KEYS_MAP = {};

    /**
     * Initialize internal data source clients.
     * Takes clients that represent asynchronous and synchronous storages,
     * though both require to implement Storage API in order to be interchangeable.
     * @param {Boolean} [loggingEnabled=false] whether to post errors to console
     * @param {Storage} [storageAsync=indexedDB client] instance of client that implements Storage API
     * @param {Storage} [storageSync=localStorage] instance of client that implements Storage API
     */
    constructor(
        loggingEnabled = false,
        storageAsync = getExternalAssessmentIndexedDBClient(),
        storageSync = localStorage,
    ) {
        this.formsClient = new CompressedStorageClient(storageAsync);
        this.cutscoresClient = new CompressedStorageClient(storageAsync);
        this.compressedResultsClient = new StorageClient(storageAsync);
        this.resultsClient = new StorageClient(storageSync);
        this.loggingEnabled = loggingEnabled;
    }

    /**
     * Filter out top level result keys that do not need to be loaded in-memory
     * @param {Object} result
     * @returns {Object} new result object without not allowed keys
     */
    filterResultDetailsKeys(result) {
        return filterKeys(result, this.RESULTS_BLACKLIST_DETAILS_KEYS);
    }

    /**
     * Filter out black-listed key from results map
     * @param {Object} results
     * @returns {Object} new results map
     */
    filterResultsKeys(results) {
        return filterKeys(results, this.RESULTS_BLACKLIST_KEYS);
    }

    /**
     * Get forms from storage. Cut item level details fields.
     * @returns {Promise<Array>} list of forms: [<form object 1> ...<form object N>]
     */
    async getForms() {
        let forms = [];
        try {
            forms = await this.formsClient.getItem(this.FORMS_KEY);
        } catch (e) {
            if (this.loggingEnabled) {
                console.warn(e);
            }
            return forms;
        }
        return forms.map(form => filterKeys(form, this.FORMS_BLACKLIST_DETAILS_KEYS));
    }

    /**
     * Get cutscores from storage.
     * Cut useless fields.
     * Rename keys for some fields.
     * Suppress error if results not found.
     * @returns {Promise<Array>} list of cutscores: [<cutscore object 1> ...<cutscore object N>]
     */
    async getCutscores() {
        let cutscores = [];
        try {
            cutscores = await this.cutscoresClient.getItem(this.CUTSCORES_KEY);
        } catch (e) {
            if (this.loggingEnabled) {
                console.warn(e);
            }
            return cutscores;
        }
        return cutscores.map(cutscore =>
            filterKeys(
                cutscore,
                this.CUTSCORES_BLACKLIST_DETAILS_KEYS,
                this.CUTSCORES_OUTPUT_KEYS_MAP,
            ),
        );
    }

    /**
     * Get results created/updated in offline for all students.
     * Cut item level details before returning.
     * Suppress error if results not found.
     * @returns {Promise<Object>} results map { <result id> : <result object> }
     */
    async getOfflineStudentsResults() {
        let rawResults = {};
        try {
            rawResults = await this.resultsClient.getItem(this.OFFLINE_RESULTS_KEY);
        } catch (e) {
            if (this.loggingEnabled) {
                console.warn(e);
            }
            return rawResults;
        }
        return mapValues(
            this.filterResultsKeys(rawResults),
            flow([JSON.parse, this.filterResultDetailsKeys.bind(this)]),
        );
    }

    /**
     * Get read-only student results that are saved to server.
     * Cut item level details before returning.
     * Suppress error if results not found.
     * @param {String} studentId student to get results for
     * @returns {Promise<Object>} results map { <result id> : <result object> }
     */
    async getStudentResults(studentId) {
        let rawResults = {};
        try {
            rawResults = await this.compressedResultsClient.getItem(
                this.getStudentResultsKey(studentId),
            );
        } catch (e) {
            if (this.loggingEnabled) {
                console.warn(e);
            }
            return rawResults;
        }

        return mapValues(
            this.filterResultsKeys(rawResults),
            flow([JSON.parse, decompress, JSON.parse, this.filterResultDetailsKeys.bind(this)]),
        );
    }

    /**
     * Get results for a list of students.
     * Resolve offline vs. server results conflicts in favor of offline.
     * @param {Array<String>} studentIds students to get results for
     * @returns {Promise<Array>} list of student results: [<result 1> ... <result N>]
     */
    async getStudentsResults(studentIds) {
        const results = await Promise.all([
            ...studentIds.map(studentId => this.getStudentResults(studentId)),
            this.getOfflineStudentsResults(), // place offline results last to override server results
        ]);

        return values(
            results.reduce(
                (resultsMap, studentResults) => ({ ...resultsMap, ...studentResults }),
                {},
            ),
        );
    }
}

export default AssessmentStorageClient;
